import React, {
  useState,
  useCallback,
  useContext,
  createContext,
  useMemo,
} from 'react';
import api from 'services';
import { useNewToast } from 'hooks/newToast';
import { useSafra } from 'hooks/providers/configuracoes/safras';
import { appWarnings } from 'utils/appWarnings';
import { formatBeneficio } from 'utils/formaters/rastreabilidade/beneficio';

const successful = {
  type: 'success',
  message: 'Benefício gerado com sucesso!',
};

const BeneficioContext = createContext({});

export function BeneficioProvider({ children }) {
  const { showToast } = useNewToast();
  const { safraAtual } = useSafra();

  const [beneficios, setBeneficios] = useState([]);
  const [beneficiosLoading, setBeneficiosLoading] = useState(false);
  const [gerarBeneficioLoading, setGerarBeneficioLoading] = useState(false);
  const [editBeneficioLoading, setEditBeneficioLoading] = useState(false);
  const [beneficio, setBeneficio] = useState({});
  const [beneficioLoading, setBeneficioLoading] = useState(false);
  const [deleteAdicionalBeneficioLoading, setDeleteAdicionalBeneficioLoading] =
    useState(false);
  const [deleteBeneficioLoading, setDeleteBeneficioLoading] = useState(false);
  const [deleteLoteBeneficioLoading, setDeleteLoteBeneficioLoading] =
    useState(false);
  const [beneficioSimplificados, setBeneficioSimplificados] = useState([]);
  const [beneficioSimplificadosLoading, setBeneficioSimplificadossLoading] =
    useState(false);
  const [gerarLoteAutomatizadoLoading, setGerarLoteAutomatizadoLoading] =
    useState(false);

  const getBeneficios = useCallback(async (filter) => {
    try {
      setBeneficiosLoading(true);
      const response = await api.beneficio().getBeneficios(filter);
      setBeneficios(response);
      setBeneficiosLoading(false);
    } catch (err) {
      setBeneficiosLoading(false);
    }
  }, []);

  const getBeneficioSimplificados = useCallback(async (filter) => {
    try {
      setBeneficioSimplificadossLoading(true);
      const response = await api.beneficio().getBeneficioSimplificados(filter);
      setBeneficioSimplificados(response);
      setBeneficioSimplificadossLoading(false);
    } catch (err) {
      setBeneficioSimplificadossLoading(false);
    }
  }, []);

  const deleteBeneficioSimplificado = useCallback(
    async (id) => {
      try {
        setDeleteBeneficioLoading(true);
        await api.beneficio().deleteBeneficioSimplificado(id);
        showToast({
          ...successful,
          message: 'Benefício removido com sucesso!',
        });
        setDeleteBeneficioLoading(false);
      } catch (err) {
        setDeleteBeneficioLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast],
  );

  const getBeneficio = useCallback(async (id) => {
    try {
      setBeneficioLoading(true);
      const response = await api.beneficio().getBeneficio(id);
      setBeneficio(response);
      setBeneficioLoading(false);
    } catch (errors) {
      setBeneficioLoading(false);
    }
  }, []);

  const resetBeneficio = useCallback(() => {
    setBeneficio({});
  }, []);

  const insertBeneficio = useCallback(
    async (dados) => {
      const tempData = formatBeneficio(dados);
      const codigoBeneficio = String(dados?.dados?.codigoBeneficio || '')
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9_-]/g, '')
        .slice(0, 10);
      const payload = {
        ...tempData,
        codigo_personalizado: codigoBeneficio || null,
        safra: dados?.safra ?? safraAtual?.id,
      };

      try {
        setGerarBeneficioLoading(true);
        const response = await api.beneficio().gerarBeneficio(payload);
        setGerarBeneficioLoading(false);
        showToast(successful);
        return response;
      } catch (err) {
        setGerarBeneficioLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast, safraAtual],
  );

  const updateBeneficio = useCallback(
    async (id, dados) => {
      try {
        setEditBeneficioLoading(true);
        const tempData = formatBeneficio(dados);
        const codigoBeneficio = String(dados?.dados?.codigoBeneficio || '')
          .trim()
          .toUpperCase()
          .replace(/[^A-Z0-9_-]/g, '')
          .slice(0, 10);
        const payload = codigoBeneficio
          ? { ...tempData, codigo_personalizado: codigoBeneficio }
          : tempData;

        const response = await api.beneficio().editBeneficio(id, payload);
        showToast({ ...successful, message: 'Benefício editado com sucesso!' });
        setEditBeneficioLoading(false);
        return response;
      } catch (err) {
        setEditBeneficioLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast],
  );

  const deleteBeneficio = useCallback(
    async (codigo) => {
      try {
        setDeleteBeneficioLoading(true);
        await api.beneficio().deleteBeneficio(codigo);
        setBeneficios(beneficios.filter((item) => item.id !== codigo));
        setDeleteBeneficioLoading(false);
        getBeneficios();
        showToast({
          ...successful,
          message: 'Processo de Benefício removido com sucesso!',
        });
      } catch (err) {
        setDeleteBeneficioLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast, beneficios, getBeneficios],
  );

  const deleteAdicional = useCallback(
    async (idBen, idAdd) => {
      try {
        setDeleteAdicionalBeneficioLoading(true);
        await api.beneficio().deleteAdicional(idBen, idAdd);
        setDeleteAdicionalBeneficioLoading(false);
        showToast({
          ...successful,
          message: 'Adicional deletado com sucesso!',
        });
      } catch (err) {
        setDeleteAdicionalBeneficioLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast],
  );

  const deleteLote = useCallback(
    async (idBen, idLote) => {
      try {
        setDeleteLoteBeneficioLoading(true);
        await api.beneficio().deleteLote(idBen, idLote);
        setDeleteLoteBeneficioLoading(false);
        showToast({ ...successful, message: 'Lote deletado com sucesso!' });
      } catch (err) {
        setDeleteLoteBeneficioLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast],
  );

  const gerarLoteAutomatizado = useCallback(
    async (dados) => {
      try {
        setGerarLoteAutomatizadoLoading(true);
        const response = await api.beneficio().gerarLoteAutomatizado({
          ...dados,
          safra: dados?.safra ?? safraAtual?.id,
        });
        setGerarLoteAutomatizadoLoading(false);
        showToast(successful);
        return response;
      } catch (err) {
        setGerarLoteAutomatizadoLoading(false);
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      }
    },
    [showToast, safraAtual],
  );

  const value = useMemo(
    () => ({
      deleteLoteBeneficioLoading,
      deleteLote,
      deleteAdicional,
      deleteAdicionalBeneficioLoading,
      gerarBeneficioLoading,
      insertBeneficio,
      editBeneficioLoading,
      updateBeneficio,
      deleteBeneficioLoading,
      deleteBeneficio,
      beneficiosLoading,
      beneficios,
      beneficio,
      beneficioLoading,
      getBeneficio,
      getBeneficios,
      resetBeneficio,
      beneficioSimplificados,
      beneficioSimplificadosLoading,
      getBeneficioSimplificados,
      deleteBeneficioSimplificado,
      gerarLoteAutomatizado,
      gerarLoteAutomatizadoLoading,
    }),
    [
      deleteLoteBeneficioLoading,
      deleteLote,
      deleteAdicional,
      deleteAdicionalBeneficioLoading,
      gerarBeneficioLoading,
      insertBeneficio,
      editBeneficioLoading,
      updateBeneficio,
      deleteBeneficioLoading,
      deleteBeneficio,
      beneficiosLoading,
      beneficios,
      beneficio,
      beneficioLoading,
      getBeneficio,
      getBeneficios,
      resetBeneficio,
      beneficioSimplificados,
      beneficioSimplificadosLoading,
      getBeneficioSimplificados,
      deleteBeneficioSimplificado,
      gerarLoteAutomatizado,
      gerarLoteAutomatizadoLoading,
    ],
  );

  return (
    <BeneficioContext.Provider value={value}>
      {children}
    </BeneficioContext.Provider>
  );
}

export function useBeneficio() {
  const context = useContext(BeneficioContext);

  if (!context)
    throw new Error('useBeneficio must be used within an AuthProvider');

  return context;
}
